import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error,
      errorInfo
    });

    // Log to error reporting service
    console.error('Error caught by boundary:', error, errorInfo);

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Send to analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: `${error.message} ${errorInfo.componentStack}`,
        fatal: true
      });
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    window.location.href = '/';
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="error-boundary" role="alert" aria-live="assertive">
          <div className="error-boundary-content">
            <AlertCircle className="error-boundary-icon" aria-hidden="true" />

            <h1 className="error-boundary-title">
              Something went wrong
            </h1>

            <p className="error-boundary-message">
              We apologize for the inconvenience. An unexpected error has occurred.
            </p>

            {this.state.error && (
              <details className="text-left">
                <summary className="cursor-pointer text-sm text-gray-400 hover:text-white mb-2">
                  Error Details (for developers)
                </summary>
                <pre className="error-boundary-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="btn-primary flex items-center justify-center gap-2"
                aria-label="Reload the page"
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                Reload Page
              </button>

              <button
                onClick={this.handleGoHome}
                className="btn-secondary flex items-center justify-center gap-2"
                aria-label="Go to home page"
              >
                <Home className="w-4 h-4" aria-hidden="true" />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Async error boundary for data fetching
interface AsyncErrorBoundaryProps {
  children: ReactNode;
  error: Error | null;
  reset: () => void;
}

export function AsyncErrorBoundary({ children, error, reset }: AsyncErrorBoundaryProps) {
  if (error) {
    return (
      <div className="glass-card p-6 text-center space-y-4" role="alert">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-white">Failed to load data</h2>
        <p className="text-gray-400 text-sm">{error.message}</p>
        <button onClick={reset} className="btn-primary">
          Try Again
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

export default ErrorBoundary;
